import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { HotelRuleSearchCriteriaUi } from '../model/hotel-rule-search-criteria';
import * as HotelModel from '../model';
import { Store, select } from '@ngrx/store';
import { Router } from '@angular/router';
import { NavigationService } from '../../service/core/navigation.service';
import {
  SearchHotelRuleAction,
  ActivateHotelRuleDisplayAction,
  DeleteHotelRuleDisplayNotificationAction,
  ClearHotelRulesAction,
  DeleteHotelRuleSearchNotificationAction,
  DeleteHotelRuleAction,
  StartHotelModificationAction,
  CopyHotelAction,
  DeleteHotelRuleCreateNotificationAction
} from '../../store/hotel/hotels-action';
import { BehaviorSubject, map, Observable, Subscription, combineLatest } from 'rxjs';
import { INCLUDE_ACTION } from '../model';
import { UppNotification } from '../../model/notification';
import {
  selectHotelRuleSearchSending,
  selectHotelRules,
  selectHotelRuleSearchNotification,
  selectHotelRuleDeleteSending,
  selectHotelCurrentPage,
  selectHotelPageSize,
  selectHotelTotalCount
} from '../../store/hotel/hotels-selector';
import {
  NotificationMessages,
  UppNotificationComponent
} from '../../components/upp-notification/upp-notification.component';
import { HotelsSearchCriteriaComponent } from './hotels-search-criteria/hotels-search-criteria.component';
import { UserDetailsService } from '../../service/user-details.service';
import { UppComponentNames } from '../../service/model';
import {
  BasicSearchResultTableComponent,
  DisplayableField,
  EntryType
} from '../../base/search/basic-search-result-table/basic-search-result-table.component';
import { PERMISSION_MANAGE_HOTEL } from '../../service/user-permissions.service';
import { ConfigurationService } from '../../service/configuration/configuration.service';
import { FeatureFlags } from '../../core/util/resources';
import { AsyncPipe } from '@angular/common';
import { LoadingAlertComponent } from '../../components/loading-alert/loading-alert.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HotelService } from '../../service/hotel.service';
import { HistoryModalComponent } from '../../components/history-modal/history-modal.component';
import { HistoryRuleRecord } from '../../model/history-rule-record';
import { HotelsState } from '../../store/hotel/hotels-state';
import { AgGridSearchTableComponent } from '../../base/search/ag-grid-search-table/ag-grid-search-table.component';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridReadyEvent } from 'ag-grid-community';
import { TableActionsComponent } from '../../components/table-actions/table-actions.component';
import { CopyToClipboardButtonComponent } from '../../components/copy-to-clipboard-button/copy-to-clipboard-button.component';
import { InfoHeaderComponent } from '../../components/info-header/info-header.component';

export const displayableFields: DisplayableField[] = [
  {
    value: 'pointOfSaleNames',
    label: 'POS'
  },
  {
    value: 'destinationNames',
    label: 'Market'
  },
  {
    value: 'actionType',
    label: 'Action type'
  },
  {
    value: 'description',
    label: 'Description'
  },
  {
    value: 'ruleStatusActive',
    label: 'Rule Status'
  }
];

export const displayableFieldsV2WithId: DisplayableField[] = [
  {
    value: 'id',
    label: 'Rule ID',
    isSortable: true,
    hasCopyToClipboard: true,
    displayShortened: true
  },
  {
    value: 'name',
    label: 'Name',
    isSortable: true
  },
  {
    value: 'pointOfSaleNames',
    label: 'Point of sale'
  },
  {
    value: 'destinationNames',
    label: 'Markets'
  },
  {
    value: 'ruleStatusActive',
    label: 'Rule status',
    isSortable: true
  },
  {
    value: 'weight',
    label: 'Rule weight',
    isSortable: true
  },
  {
    value: 'description',
    label: 'Description'
  }
];

export const displayableFieldsV2WithoutId: DisplayableField[] = [
  {
    value: 'name',
    label: 'Name',
    isSortable: true
  },
  {
    value: 'pointOfSaleNames',
    label: 'Point of sale'
  },
  {
    value: 'destinationNames',
    label: 'Markets'
  },
  {
    value: 'ruleStatusActive',
    label: 'Rule status',
    isSortable: true
  },
  {
    value: 'weight',
    label: 'Rule weight',
    isSortable: true
  },
  {
    value: 'description',
    label: 'Description'
  }
];

@Component({
  selector: 'ama-ng-upp-hotels-search',
  templateUrl: './hotels-search.component.html',
  standalone: true,
  imports: [
    LoadingAlertComponent,
    UppNotificationComponent,
    HotelsSearchCriteriaComponent,
    BasicSearchResultTableComponent,
    AsyncPipe,
    AgGridAngular
  ]
})
export class HotelsSearchComponent extends AgGridSearchTableComponent implements OnInit, OnDestroy {
  @ViewChild(HotelsSearchCriteriaComponent)
  searchCriteriaComponent!: HotelsSearchCriteriaComponent;

  hotelRuleSearchSending$: Observable<boolean>;
  hotelRuleDeleteSending$: Observable<boolean | undefined>;
  hotelRules$: Observable<HotelModel.HotelRuleUi[]>;
  transformedHotelRules$: Observable<HotelModel.HotelRule[]>;
  notification$: Observable<UppNotification | undefined>;
  currentPage$: Observable<number>;
  pageSize$: Observable<number>;
  totalCount$: Observable<number>;

  currentPage = 0;
  pageSize = 10;
  private lastSearchCriteria: HotelRuleSearchCriteriaUi | null = null;

  mainMessages: NotificationMessages;

  managePermission = PERMISSION_MANAGE_HOTEL;
  displayableFields: DisplayableField[] = displayableFields;

  hotelsV2 = false;
  displayRuleHistory = false;

  troubleShootingEnabled = false;
  agGridTablesActivated = false;
  paginationEnabled = false;
  partialNameMatchEnabled = false;
  activeConfirmationRowId$ = new BehaviorSubject<string | null>(null);

  subscription: Subscription = new Subscription();

  constructor(
    store: Store<HotelsState>,
    router: Router,
    navigationService: NavigationService,
    private readonly userDetailsService: UserDetailsService,
    private readonly dfModalService: NgbModal,
    private readonly hotelService: HotelService,
    private readonly configurationService: ConfigurationService
  ) {
    super(store, router, navigationService);

    this.hotelRuleSearchSending$ = this.store.pipe(select(selectHotelRuleSearchSending));
    this.hotelRuleDeleteSending$ = this.store.pipe(select(selectHotelRuleDeleteSending));
    this.hotelRules$ = this.store.pipe(select(selectHotelRules));
    this.currentPage$ = this.store.pipe(select(selectHotelCurrentPage));
    this.pageSize$ = this.store.pipe(select(selectHotelPageSize));
    this.totalCount$ = this.store.pipe(select(selectHotelTotalCount));

    this.transformedHotelRules$ = combineLatest([
      this.hotelRules$,
      this.currentPage$,
      this.pageSize$,
      this.totalCount$,
      this.hotelRuleSearchSending$
    ]).pipe(
      map(([hotelRules, currentPage, pageSize, totalCount, isLoading]) => {
        const transformedRules = (hotelRules || [])
          .filter((hr) => hr && !hr.statusType)
          .map((hr) => this.recordToSearchResult(hr));

        if (this.paginationEnabled && totalCount > 0) {
          // Sparse array with length=totalCount so ag-grid calculates correct page count.
          // Each placeholder gets a unique __idx so getRowId never returns undefined.
          const fullArray = Array.from({ length: totalCount }, (_, i) => ({ __idx: i })) as { __idx: number }[];

          if (!isLoading && transformedRules.length > 0) {
            // Only populate with actual data when not loading
            const startIndex = currentPage * pageSize;
            transformedRules.forEach((rule, index) => {
              fullArray[startIndex + index] = rule;
            });
          }

          return fullArray;
        }

        return transformedRules;
      })
    );

    this.notification$ = this.store.pipe(select(selectHotelRuleSearchNotification));
    this.mainMessages = {
      error: $localize`:@@upp.global.text.mainErrorText:The request failed due to the following errors:`,
      warning: $localize`:@@upp.global.text.mainWarningText:Warnings were generated during the search process:`,
      success: $localize`:@@upp.global.text.mainSuccessText:The search was successful.`
    };

    this.subscription.add(
      this.configurationService.getParameter$(FeatureFlags.troubleshootingPanel).subscribe((isEnabled) => {
        this.troubleShootingEnabled = isEnabled ?? false;
      })
    );

    this.configurationService.getParameter$(FeatureFlags.agGridTables).subscribe((isEnabled) => {
      this.agGridTablesActivated = isEnabled ?? false;
    });

    this.configurationService.getParameter$(FeatureFlags.partialNameMatch).subscribe((isEnabled) => {
      this.partialNameMatchEnabled = isEnabled ?? false;
    });

    this.configurationService.getParameter$(FeatureFlags.pagination).subscribe((isEnabled) => {
      this.paginationEnabled = isEnabled ?? false;
    });
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.navigationService.setSelectedMenuTitle($localize`:@@upp.hotels.search.navigationTitle:Hotel Rule Search`);
    this.navigationService.activateHotels();
    this.navigationService.enableNavigation();
    this.subscription?.add(
      this.configurationService.getParameter$(FeatureFlags.hotelsV2).subscribe((hotelsV2) => {
        this.hotelsV2 = hotelsV2 ?? false;
        if (hotelsV2) {
          this.displayableFields = this.troubleShootingEnabled
            ? displayableFieldsV2WithId
            : displayableFieldsV2WithoutId;
        } else {
          this.displayableFields = displayableFields;
        }
      })
    );
    this.subscription.add(
      this.configurationService.getParameter$(FeatureFlags.ruleHistory)?.subscribe((ruleHistory) => {
        this.displayRuleHistory = ruleHistory ?? false;
      })
    );

    if (this.agGridTablesActivated) {
      this.initializeColumnDefs();
    }

    this.subscription.add(
      this.currentPage$.subscribe((page) => {
        this.currentPage = page;
      })
    );

    this.subscription.add(
      this.pageSize$.subscribe((size) => {
        this.pageSize = size;
      })
    );
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  searchHotelRules(searchCriteria: HotelRuleSearchCriteriaUi) {
    this.lastSearchCriteria = searchCriteria;
    this.currentPage = 0;

    if (this.partialNameMatchEnabled) {
      searchCriteria.enableFullTextSearch = true;
    }
    const request = this.createHotelRulesSearchRequest(searchCriteria);
    request.rule = this.userDetailsService.assignEntity(request.rule);
    this.store.dispatch(new SearchHotelRuleAction({ request }));
    if (this.gridApi) {
      this.gridApi.paginationGoToPage(0);
    }
  }

  createHotelRulesSearchRequest(searchCriteria: HotelRuleSearchCriteriaUi): HotelModel.HotelRuleSearchRequest {
    const request: HotelModel.HotelRuleSearchRequest = {
      version: '2.0',
      rule: HotelModel.convertToHotelSearchCriteria(searchCriteria)
    };
    if (this.paginationEnabled) {
      request.searchFollowUpRequest = {
        requestedPageNumber: String(this.currentPage + 1), // 1-based for hotels API (same as flights)
        numberOfRecords: String(this.pageSize)
      };
    }
    return request;
  }

  closeNotification() {
    this.store.dispatch(new DeleteHotelRuleSearchNotificationAction({}));
  }

  clearHotelRules() {
    this.store.dispatch(new ClearHotelRulesAction({}));
    this.router.navigate(['hotels/search']);
  }

  showHotelRule(id: string) {
    this.store.dispatch(new ActivateHotelRuleDisplayAction({ id: undefined }));
    this.store.dispatch(new DeleteHotelRuleDisplayNotificationAction({}));
    this.router.navigate(['hotels/display/' + id]); // NOSONAR
  }

  modifyHotelRule(hotelRuleId: string) {
    this.store.dispatch(new StartHotelModificationAction({ id: hotelRuleId }));
    this.router.navigate(['hotels/modify/']);
  }

  copyHotelRule(hotelRuleId: string) {
    this.store.dispatch(new CopyHotelAction({ id: hotelRuleId }));
    this.store.dispatch(new DeleteHotelRuleCreateNotificationAction({}));
    this.router.navigate(['hotels/create/']);
  }

  showHistory(event: { id: string; name: string }) {
    const modalRef = this.dfModalService.open(HistoryModalComponent, {
      size: 'xl',
      scrollable: true,
      backdrop: 'static',
      centered: true
    });

    modalRef.componentInstance.ruleName.set(event.name);
    modalRef.componentInstance.isLoading.set(true);
    modalRef.componentInstance.rowData = [];

    this.subscription.add(
      this.hotelService.getRuleHistory(event.id).subscribe({
        next: (data: HistoryRuleRecord[]) => {
          modalRef.componentInstance.rowData = data;
          modalRef.componentInstance.isLoading.set(false);
        },
        error: () => {
          modalRef.componentInstance.rowData = [];
          modalRef.componentInstance.isLoading.set(false);
        }
      })
    );
  }

  storeUnsavedHotelSearch() {
    this.searchCriteriaComponent.storeUnsavedHotelSearch();
  }

  deleteHotelRule(request: HotelModel.HotelRuleDeleteRequest) {
    this.store.dispatch(new DeleteHotelRuleAction({ request }));
  }

  getSourceComponent(): UppComponentNames {
    return UppComponentNames.HOTELS;
  }

  getEntryType(): EntryType {
    return EntryType.RULE;
  }

  recordToSearchResult(hotelRecord: HotelModel.HotelRuleUi): any {
    return {
      id: hotelRecord.id,
      name: hotelRecord.name,
      description: hotelRecord.description ? hotelRecord.description : '',
      organization: hotelRecord.organization,
      version: hotelRecord.version,
      pointOfSaleNames: this.getPosOrMarketNames(hotelRecord.hotelApplicability?.pointOfSaleNames),
      destinationNames: this.getPosOrMarketNames(hotelRecord.hotelApplicability?.destinationNames),
      actionType: calculateActionType(hotelRecord),
      ruleStatusActive: hotelRecord.active,
      weight: hotelRecord.weight
    };
  }

  entryToUpdateCreator(
    id: string,
    name: string,
    organization: string,
    version: string,
    ruleVersion: number
  ): HotelModel.HotelRuleDeleteRequest {
    return {
      version,
      rule: {
        id,
        name,
        version: ruleVersion
      }
    };
  }

  entryToUpdateNameResolver(entryToUpdate: HotelModel.HotelRuleDeleteRequest) {
    return entryToUpdate.rule.name;
  }

  getPosOrMarketNames(names?: string[]): string {
    if (names === undefined || names.length === 0) {
      return '';
    }
    return names.toString();
  }

  getDisplayHandler(): (data: unknown) => void {
    return (data: unknown) => this.showHotelRule((data as HotelModel.HotelRule).id ?? '');
  }

  getModifyHandler(): (data: unknown) => void {
    return (data: unknown) => this.modifyHotelRule((data as HotelModel.HotelRule).id ?? '');
  }

  getCopyHandler(): (data: unknown) => void {
    return (data: unknown) => this.copyHotelRule((data as HotelModel.HotelRule).id ?? '');
  }

  getDeleteHandler(): (data: unknown) => void {
    return (data: unknown) => {
      const hotelRule = data as HotelModel.HotelRule;
      const deleteRequest = {
        version: HotelModel.HOTEL_RULE_DELETE_REQUEST_VERSION,
        rule: {
          id: hotelRule.id ?? '',
          version: hotelRule.version ?? '1'
        }
      } as HotelModel.HotelRuleDeleteRequest;

      this.deleteHotelRule(deleteRequest);
    };
  }

  getShowHistoryHandler(): (data: unknown) => void {
    return (data: unknown) => {
      const hotelRule = data as HotelModel.HotelRule;
      this.showHistory({ id: hotelRule.id ?? '', name: hotelRule.name ?? '' });
    };
  }

  getDeleteSendingObservable(): Observable<boolean> {
    return this.hotelRuleDeleteSending$.pipe(map((value) => value ?? false));
  }

  getNameField(): string {
    return 'name';
  }

  setActiveConfirmationRowId(rowId: string | null) {
    this.activeConfirmationRowId$.next(rowId);
  }

  override onGridReady(params: GridReadyEvent): void {
    super.onGridReady(params);

    // Only sync the page size. With getRowId defined, ag-grid does delta row
    // updates and does NOT reset the page when rowData changes, so
    // paginationGoToPage() is not needed and causes interference.
    this.subscription.add(
      this.pageSize$.subscribe((size) => {
        if (!this.gridApi) {
          return;
        }
        if (size !== this.gridApi.paginationGetPageSize()) {
          this.gridApi.setGridOption('paginationPageSize', size);
        }
      })
    );
  }

  private initializeColumnDefs(): void {
    const dataColumns = this.displayableFields
      .filter((field) => field.value !== 'description')
      .map((field) => {
        const colDef: ColDef = {
          field: field.value,
          headerName: field.label,
          sortable: field.isSortable ?? false,
          unSortIcon: field.isSortable ?? false,
          resizable: false,
          suppressMovable: true,
          autoHeight: true
        };

        if (field.value === 'ruleStatusActive') {
          colDef.valueGetter = (params) => (params.data?.ruleStatusActive ? 'Active' : 'Inactive');
        }

        if (field.value === 'weight') {
          colDef.headerComponent = InfoHeaderComponent;
          colDef.headerComponentParams = {
            tooltipText: $localize`:@@upp.hotels.search.table.ruleWeightTooltip:Rules with the highest weight have priority`
          };
          colDef.comparator = (valueA, valueB) => Number(valueA) - Number(valueB);
        }

        if (field.value === 'id') {
          colDef.cellRenderer = CopyToClipboardButtonComponent;
          colDef.minWidth = 200;
        }

        return colDef;
      });

    this.columnDefs = [
      ...dataColumns,
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        resizable: false,
        suppressMovable: true,
        autoHeight: true,
        flex: 2,
        maxWidth: 200,
        minWidth: 200,
        wrapText: true,
        cellRenderer: TableActionsComponent,
        cellRendererParams: {
          showHistoryButton: this.displayRuleHistory
        }
      }
    ];

    // Configure gridOptions with backend pagination settings
    this.gridOptions = {
      ...this.gridOptions,
      // Placeholder rows have no .id; fall back to their array index so getRowId never returns undefined
      getRowId: (params) => params.data.id ?? `__ph_${params.data.__idx}`,
      // Hide placeholder rows visually — they only exist to give ag-grid the correct total row count
      getRowClass: (params) => (params.data?.id ? '' : 'ag-row-hidden'),
      postSortRows: (params) => {
        this.handlePostSortRowsForPagination(params, this.paginationEnabled);
      },
      onPaginationChanged: () => {
        if (!this.paginationEnabled || !this.gridApi || !this.lastSearchCriteria) {
          return;
        }

        const newPage = this.gridApi.paginationGetCurrentPage();
        const newPageSize = this.gridApi.paginationGetPageSize();

        // Only fetch if page or size actually changed
        if (newPage !== this.currentPage || newPageSize !== this.pageSize) {
          this.currentPage = newPage;
          this.pageSize = newPageSize;

          // Dispatch search action directly with updated pagination
          const request = this.createHotelRulesSearchRequest(this.lastSearchCriteria);
          request.rule = this.userDetailsService.assignEntity(request.rule);
          this.store.dispatch(new SearchHotelRuleAction({ request }));
        }
      }
    };
  }
}

const calculateActionType = (rule: HotelModel.HotelRuleUi): string => {
  if (!rule || rule.action === undefined) {
    return '';
  }
  if (rule.action === INCLUDE_ACTION) {
    return $localize`:@@upp.hotels.search.table.include:Incl.`;
  } else if (rule.lightExclude) {
    return $localize`:@@upp.hotels.search.table.lightExclude:Light Excl.`;
  }
  return $localize`:@@upp.hotels.search.table.exclude:Excl.`;
};
